import { useParams, useNavigate, Link } from 'react-router-dom';
import { Tag, Badge, Button } from '@caos/ca-component-library';
import { formatDate, getRelatedSkills, getRelatedMcpServers } from '../lib/marketplace';
import { useMcpRegistry } from '../context/McpRegistryContext';
import { useManifest } from '../context/ManifestContext';
import SkillCard from '../components/SkillCard';
import McpServerCard from '../components/McpServerCard';
import CodeBlock from '../components/CodeBlock';
import EmptyState from '../components/EmptyState';
import usePageTitle from '../hooks/usePageTitle';
import './McpDetailPage.css';

export default function McpDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { servers, loading } = useMcpRegistry();
  const { skills } = useManifest();

  const server = servers.find((s) => s.id === id);
  usePageTitle(server?.name);

  if (loading) {
    return (
      <div className="mcp-detail-page">
        <p>Loading...</p>
      </div>
    );
  }
  const serverTags = server?.tags ?? [];
  const relatedSkills = getRelatedSkills(serverTags, id ?? '', skills, 2);
  const relatedMcp = getRelatedMcpServers(serverTags, id ?? '', servers, 2);
  const hasRelated = relatedSkills.length > 0 || relatedMcp.length > 0;

  if (!server) {
    return (
      <div className="mcp-detail-page">
        <EmptyState
          title="MCP Server not found"
          description={`No MCP server with ID "${id}" exists in the registry.`}
          action={
            <Button size="sm" onClick={() => navigate('/')}>
              Back to marketplace
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="mcp-detail-page">
      <nav className="mcp-detail-breadcrumb">
        <Link to="/" className="mcp-detail-back">
          &larr; Back to marketplace
        </Link>
      </nav>

      <header className="mcp-detail-header">
        <div className="mcp-detail-title-row">
          <h1 className="mcp-detail-name">{server.name}</h1>
          {server.environment && (
            <span className="mcp-card-environment">{server.environment.toLowerCase()}</span>
          )}
          <Badge
            size="sm"
            shape="capsule"
            style={{ '--badge-bg': 'var(--ca-primary)' } as React.CSSProperties}
          >
            MCP Server
          </Badge>
        </div>
        {server.description && (
          <p className="mcp-detail-description">{server.description}</p>
        )}
      </header>

      <div className="mcp-detail-content">
        <section className="mcp-detail-section">
          <h2 className="mcp-detail-section-title">Details</h2>
          <dl className="mcp-detail-meta">
            <div className="mcp-detail-meta-item">
              <dt>Owner</dt>
              <dd>{server.owner}</dd>
            </div>
            <div className="mcp-detail-meta-item">
              <dt>Last updated</dt>
              <dd>{formatDate(server.last_updated)}</dd>
            </div>
            <div className="mcp-detail-meta-item">
              <dt>Endpoint URL</dt>
              <dd>
                <code>{server.endpoint_url}</code>
              </dd>
            </div>
          </dl>
        </section>

        {server.tags?.length > 0 && (
          <section className="mcp-detail-section">
            <h2 className="mcp-detail-section-title">Tags</h2>
            <div className="mcp-detail-tools">
              {server.tags.map((tag) => (
                <Tag key={tag} size="md" border>
                  {tag}
                </Tag>
              ))}
            </div>
          </section>
        )}

        {server.tools.length > 0 && (
          <section className="mcp-detail-section">
            <h2 className="mcp-detail-section-title">Available Tools</h2>
            <p className="mcp-detail-tools-intro">
              This server exposes the following tools to Claude Code via MCP.
            </p>
            <div className="mcp-detail-tools">
              {server.tools.map((tool) => (
                <Tag key={tool} size="md" border>
                  {tool}
                </Tag>
              ))}
            </div>
          </section>
        )}

        <section className="mcp-detail-section">
          <h2 className="mcp-detail-section-title">Configuration</h2>

          <p className="mcp-detail-config-intro">
            There are a few ways to use this MCP server.
          </p>

          <div className="mcp-detail-config-methods">
            <div className="mcp-detail-config-method">
              <h3>Claude Code via CLI</h3>
              <p>Run the following command in your terminal:</p>
              <CodeBlock code={`claude mcp add --transport http ${server.name} ${server.endpoint_url}`} />
            </div>

            <div className="mcp-detail-config-method">
              <h3>Claude Code via settings</h3>
              <p>
                Add the following entry to your <code>~/.claude.json</code> under <code>mcpServers</code>:
              </p>
              <CodeBlock code={`"${server.name}": {\n  "type": "http",\n  "url": "${server.endpoint_url}"\n}`} />
            </div>

            <div className="mcp-detail-config-method">
              <h3>Universal MCP JSON</h3>
              <p>Use this with any AI client that supports remote HTTP MCP servers:</p>
              <CodeBlock code={`{\n  "mcpServers": {\n    "${server.name}": {\n      "type": "http",\n      "url": "${server.endpoint_url}"\n    }\n  }\n}`} />
            </div>
          </div>
        </section>

        {hasRelated && (
          <section className="mcp-detail-section">
            <h2 className="mcp-detail-section-title">Related</h2>
            <div className="mcp-detail-related-grid">
              {relatedSkills.map((s) => <SkillCard key={s.id} skill={s} />)}
              {relatedMcp.map((s) => <McpServerCard key={s.id} server={s} />)}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
